// TelcoTrack — ROZVADEC cassette catalog (KM4 / KM5 / KM6) per PI-0045 + PI-0053 + PI-0056.
// Cassettes occupy rack positions; terminations live in cassette slots (weld-layout v2).
// Splitters in KM5: SAP 326278 (1×1:16), 326279 (2×1:8), plus 1×1:4 / 2×1:4 (no CETIN SAP).
// KM5/SC and KM5/LC are first-class types. Legacy id `KM5` aliases to `KM5_SC`.

import type { RozvadecSplitterRatio } from './rozvadec-cabinet.constants';

export const ROZVADEC_CASSETTE_TYPE_IDS = [
  'KM4',
  'KM5_SC',
  'KM5_LC',
  'KM5_SPLITTER_1X16',
  'KM5_SPLITTER_2X8',
  'KM5_SPLITTER_1X4',
  'KM5_SPLITTER_2X4',
  'KM6',
] as const;
export type RozvadecCassetteTypeId = (typeof ROZVADEC_CASSETTE_TYPE_IDS)[number];

/** Legacy weld-layout id written before KM5/SC vs KM5/LC split. */
export const ROZVADEC_CASSETTE_TYPE_ALIASES = {
  KM5: 'KM5_SC',
} as const satisfies Record<string, RozvadecCassetteTypeId>;

export const ROZVADEC_CASSETTE_SPANS = [1, 2] as const;
export type RozvadecCassetteSpan = (typeof ROZVADEC_CASSETTE_SPANS)[number];

export const ROZVADEC_CASSETTE_ADAPTER_FAMILIES = ['SC', 'LC'] as const;
export type RozvadecCassetteAdapterFamily = (typeof ROZVADEC_CASSETTE_ADAPTER_FAMILIES)[number];

export const ROZVADEC_CASSETTE_CONNECTOR_POLARITIES = ['a', 'b'] as const;
export type RozvadecCassetteConnectorPolarity = (typeof ROZVADEC_CASSETTE_CONNECTOR_POLARITIES)[number];

/** Content a cassette accepts. Weld cassettes host direct welds only (no terminations). */
export const ROZVADEC_CASSETTE_CONTENT_KINDS = ['weld', 'connector', 'splitter'] as const;
export type RozvadecCassetteContentKind = (typeof ROZVADEC_CASSETTE_CONTENT_KINDS)[number];

export type RozvadecCassettePreinstalledSplitters = {
  count: number;
  ratio: RozvadecSplitterRatio;
};

export interface RozvadecCassetteTypeDef {
  id: RozvadecCassetteTypeId;
  nazev: string;
  /** Print label on D5 (“Typ kazety”) — only the start/upper occupied slot. */
  printLabel: string;
  /** KM6 has no standalone SAP — cassettes ship inside the PON32 cabinet (PI-0045). */
  sapCode: string | null;
  /** Rack positions occupied. Span-2 cassettes anchor at `position` and also occupy `position + 1`. */
  span: RozvadecCassetteSpan;
  /** Physical weld tray capacity (KM4/KM6 trays; KM5 pigtail welds). */
  weldCapacity: number;
  /** Physical adapter bodies (plain KM5 only). Null = no free adapters. */
  adapterCount: number | null;
  /** Connector ports: SC 12 simplex, LC 24 (12 duplex × a/b). */
  connectorPortCount: number | null;
  adapterFamily: RozvadecCassetteAdapterFamily | null;
  /** Factory-installed splitters (KM5 SAP variants only). */
  preinstalledSplitters: RozvadecCassettePreinstalledSplitters | null;
  contentKinds: readonly RozvadecCassetteContentKind[];
}

export const ROZVADEC_CASSETTE_TYPES: readonly RozvadecCassetteTypeDef[] = [
  {
    id: 'KM4',
    nazev: 'Kazeta KM4 (provařovací)',
    printLabel: 'KM4',
    sapCode: '326275',
    span: 1,
    weldCapacity: 24,
    adapterCount: null,
    connectorPortCount: null,
    adapterFamily: null,
    preinstalledSplitters: null,
    contentKinds: ['weld'],
  },
  {
    id: 'KM5_SC',
    nazev: 'Kazeta KM5/SC (konektorová)',
    printLabel: 'KM5/SC',
    sapCode: '326276',
    span: 2,
    weldCapacity: 24,
    adapterCount: 12,
    connectorPortCount: 12,
    adapterFamily: 'SC',
    preinstalledSplitters: null,
    contentKinds: ['connector'],
  },
  {
    id: 'KM5_LC',
    nazev: 'Kazeta KM5/LC (konektorová duplex)',
    printLabel: 'KM5/LC',
    sapCode: '326276',
    span: 2,
    weldCapacity: 24,
    adapterCount: 12,
    connectorPortCount: 24,
    adapterFamily: 'LC',
    preinstalledSplitters: null,
    contentKinds: ['connector'],
  },
  {
    id: 'KM5_SPLITTER_1X16',
    nazev: 'Kazeta KM5 / splitter 1×1:16',
    printLabel: 'KM5 / splitter 1×1:16',
    sapCode: '326278',
    span: 2,
    weldCapacity: 24,
    adapterCount: null,
    connectorPortCount: null,
    adapterFamily: null,
    preinstalledSplitters: { count: 1, ratio: '1:16' },
    contentKinds: ['splitter'],
  },
  {
    id: 'KM5_SPLITTER_2X8',
    nazev: 'Kazeta KM5 / 2× splitter 1:8',
    printLabel: 'KM5 / 2× splitter 1:8',
    sapCode: '326279',
    span: 2,
    weldCapacity: 24,
    adapterCount: null,
    connectorPortCount: null,
    adapterFamily: null,
    preinstalledSplitters: { count: 2, ratio: '1:8' },
    contentKinds: ['splitter'],
  },
  {
    id: 'KM5_SPLITTER_1X4',
    nazev: 'Kazeta KM5 / splitter 1×1:4',
    printLabel: 'KM5 / splitter 1×1:4',
    sapCode: null,
    span: 2,
    weldCapacity: 24,
    adapterCount: null,
    connectorPortCount: null,
    adapterFamily: null,
    preinstalledSplitters: { count: 1, ratio: '1:4' },
    contentKinds: ['splitter'],
  },
  {
    id: 'KM5_SPLITTER_2X4',
    nazev: 'Kazeta KM5 / 2× splitter 1:4',
    printLabel: 'KM5 / 2× splitter 1:4',
    sapCode: null,
    span: 2,
    weldCapacity: 24,
    adapterCount: null,
    connectorPortCount: null,
    adapterFamily: null,
    preinstalledSplitters: { count: 2, ratio: '1:4' },
    contentKinds: ['splitter'],
  },
  {
    id: 'KM6',
    nazev: 'Kazeta KM6 (provařovací, PON32)',
    printLabel: 'KM6',
    sapCode: null,
    span: 1,
    weldCapacity: 24,
    adapterCount: null,
    connectorPortCount: null,
    adapterFamily: null,
    preinstalledSplitters: null,
    contentKinds: ['weld'],
  },
];

const CASSETTE_TYPE_BY_ID: ReadonlyMap<RozvadecCassetteTypeId, RozvadecCassetteTypeDef> = new Map(
  ROZVADEC_CASSETTE_TYPES.map((def) => [def.id, def]),
);

export function canonicalRozvadecCassetteTypeId(
  value: string | null | undefined,
): RozvadecCassetteTypeId | null {
  const key = (value ?? '').trim();
  if (!key) return null;
  if ((ROZVADEC_CASSETTE_TYPE_IDS as readonly string[]).includes(key)) {
    return key as RozvadecCassetteTypeId;
  }
  const aliased = (ROZVADEC_CASSETTE_TYPE_ALIASES as Record<string, RozvadecCassetteTypeId>)[key];
  return aliased ?? null;
}

export function isRozvadecCassetteTypeId(
  value: string | null | undefined,
): value is RozvadecCassetteTypeId {
  return canonicalRozvadecCassetteTypeId(value) != null;
}

export function getRozvadecCassetteType(
  id: string | null | undefined,
): RozvadecCassetteTypeDef | undefined {
  const canonical = canonicalRozvadecCassetteTypeId(id);
  if (!canonical) return undefined;
  return CASSETTE_TYPE_BY_ID.get(canonical);
}

/** Weld tray cassette — hosts direct welds, no terminations. */
export function isRozvadecWeldTrayCassette(id: string | null | undefined): boolean {
  const def = getRozvadecCassetteType(id);
  return def != null && def.contentKinds.length === 1 && def.contentKinds[0] === 'weld';
}

/** 1-based weld seats on a provařovací kazeta (KM4 / KM6). Zero for connector/splitter cassettes. */
export function rozvadecWeldTraySeatCount(cassetteType: string | null | undefined): number {
  if (!isRozvadecWeldTrayCassette(cassetteType)) return 0;
  return getRozvadecCassetteType(cassetteType)?.weldCapacity ?? 0;
}

export function isRozvadecWeldTraySeat(
  cassetteType: string | null | undefined,
  seat: number | null | undefined,
): boolean {
  const count = rozvadecWeldTraySeatCount(cassetteType);
  return count > 0 && typeof seat === 'number' && Number.isInteger(seat) && seat >= 1 && seat <= count;
}

/** Print / editor label — `Svár 7`. */
export function rozvadecWeldTraySeatLabel(seat: number): string {
  return `Svár ${seat}`;
}

export function isRozvadecKm5FamilyCassette(id: string | null | undefined): boolean {
  const def = getRozvadecCassetteType(id);
  return def != null && def.span === 2;
}

/** 0-based rack cells occupied by a cassette anchored at `position`. */
export function rozvadecCassetteOccupiedPositions(
  cassetteType: string | null | undefined,
  position: number,
): number[] {
  const def = getRozvadecCassetteType(cassetteType);
  if (!def || !Number.isInteger(position) || position < 0) return [];
  const positions: number[] = [];
  for (let offset = 0; offset < def.span; offset += 1) {
    positions.push(position + offset);
  }
  return positions;
}

/**
 * 1-based physical positions occupied by a cassette.
 * KM5: `display_position = start`, `occupied = [start, start+1]`.
 */
export function rozvadecCassetteOccupiedDisplayPositions(
  cassetteType: string | null | undefined,
  position: number,
): number[] {
  return rozvadecCassetteOccupiedPositions(cassetteType, position).map((cell) => cell + 1);
}

/**
 * 1-based per-section label of the cassette's start (upper) occupied rack slot.
 * Span-2 KM5 at numbering `position` 0 occupying slots 1–2 is labeled **1**, not 2.
 * MUK physical labels (left 1–24, right 25–48) live in `rozvadecMukRackDisplayNumber`.
 */
export function rozvadecCassetteDisplayPosition(
  cassetteType: string | null | undefined,
  position: number,
  _fillFrom?: 'bottom' | 'top',
): number | null {
  const def = getRozvadecCassetteType(cassetteType);
  if (!def || !Number.isInteger(position) || position < 0) return null;
  return position + 1;
}

/** Adapter body 1..12 from a 1-based connector port ordinal (SC port = adapter). */
export function rozvadecKm5AdapterPosition(
  adapterFamily: RozvadecCassetteAdapterFamily | null | undefined,
  portOrdinal: number,
): number | null {
  if (!Number.isInteger(portOrdinal) || portOrdinal < 1) return null;
  if (adapterFamily === 'LC') return Math.ceil(portOrdinal / 2);
  if (adapterFamily === 'SC') return portOrdinal;
  return null;
}

/** LC polarity from port ordinal: odd → a, even → b. SC has no polarity. */
export function rozvadecKm5ConnectorPolarity(
  adapterFamily: RozvadecCassetteAdapterFamily | null | undefined,
  portOrdinal: number,
): RozvadecCassetteConnectorPolarity | null {
  if (adapterFamily !== 'LC' || !Number.isInteger(portOrdinal) || portOrdinal < 1) return null;
  return portOrdinal % 2 === 1 ? 'a' : 'b';
}

/**
 * Cell index only. SC: `5`. LC: `5b` (slot 1 = `1a`, slot 2 = `1b`).
 * Same adapter + polarity mapping as `formatRozvadecKm5ConnectorLabel`.
 */
export function formatRozvadecKm5PositionLabel(
  adapterFamily: RozvadecCassetteAdapterFamily | null | undefined,
  portOrdinal: number,
): string | null {
  const adapter = rozvadecKm5AdapterPosition(adapterFamily, portOrdinal);
  if (adapter == null) return null;
  if (adapterFamily === 'SC') return String(adapter);
  const polarity = rozvadecKm5ConnectorPolarity(adapterFamily, portOrdinal);
  if (!polarity) return null;
  return `${adapter}${polarity}`;
}

/**
 * Print connector id. SC: `SC-5`. LC: `LC-5b`. Never `LC-1`…`LC-24`.
 */
export function formatRozvadecKm5ConnectorLabel(
  adapterFamily: RozvadecCassetteAdapterFamily | null | undefined,
  portOrdinal: number,
): string | null {
  const position = formatRozvadecKm5PositionLabel(adapterFamily, portOrdinal);
  if (!position || !adapterFamily) return null;
  return `${adapterFamily}-${position}`;
}

export function isValidRozvadecKm5LcLabel(label: string | null | undefined): boolean {
  const value = (label ?? '').trim();
  return /^LC-(?:[1-9]|1[0-2])[ab]$/.test(value);
}
